import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Banknote, HeartPulse, Gavel } from "lucide-react";
import SEO from "@/components/SEO";
import { DAMAGES } from "@/data/content";

const ICONS = {
    economic: Banknote,
    "non-economic": HeartPulse,
    punitive: Gavel,
};

export default function Damages() {
    const { hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.slice(1));
            if (el)
                setTimeout(
                    () =>
                        el.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        }),
                    100,
                );
        }
    }, [hash]);

    return (
        <>
            <SEO
                title="What You Can Recover — Damages in a California Personal Injury Case"
                description="Economic, non-economic, and punitive damages explained. What a California personal injury claim can recover: medical bills, lost income, pain and suffering, and — in the worst cases — punitive damages for malice, oppression, or fraud under Civil Code § 3294."
                path="/damages"
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "What You Can Recover", path: "/damages" },
                ]}
            />

            {/* HERO */}
            <section
                data-testid="damages-hero"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-9">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            What You Can Recover
                        </div>
                        <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#F7F5EB] leading-[0.95] font-light">
                            Damages —{" "}
                            <span className="italic text-[#D4AF37]">
                                the full measure
                            </span>{" "}
                            of the harm.
                        </h1>
                        <p className="mt-8 text-lg text-[#9FB3D4] max-w-3xl leading-relaxed">
                            "Damages" is the legal word for the money a court
                            can award when someone else's negligence
                            caused you harm. California recognizes multiple
                            distinct categories. Knowing the difference — and
                            documenting all categories correctly — is often the
                            difference between a quick lowball offer and a full
                            recovery.
                        </p>
                        <div
                            className="tldr mt-8 max-w-3xl border-l-2 border-[#D4AF37] pl-5 text-[#F7F5EB]/85 text-sm leading-relaxed"
                            data-speakable
                        >
                            <span className="block text-[#D4AF37] text-[10px] uppercase tracking-[0.28em] font-bold mb-2">
                                Quick Answer
                            </span>
                            California personal injury law recognizes three
                            categories of damages. <strong>Economic damages</strong>{" "}
                            cover measurable financial losses (medical bills,
                            lost wages, property damage). <strong>Non-economic damages</strong>{" "}
                            cover the human cost of injury (pain and suffering,
                            mental anguish, loss of enjoyment of life).{" "}
                            <strong>Punitive damages</strong> are reserved for
                            the worst conduct — malice, oppression, or fraud —
                            under Civil Code § 3294.
                        </div>
                    </div>
                </div>
            </section>

            {/* DAMAGES SECTIONS */}
            <section className="border-b border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 lg:py-20">
                    {DAMAGES.map((d, i) => {
                        const Icon = ICONS[d.slug];
                        return (
                            <article
                                key={d.slug}
                                id={d.slug}
                                data-testid={`damages-${d.slug}`}
                                className={`grid lg:grid-cols-12 gap-10 py-14 lg:py-20 ${
                                    i !== 0
                                        ? "border-t border-[#1F3458]"
                                        : ""
                                }`}
                            >
                                <div className="lg:col-span-5">
                                    <Icon
                                        size={36}
                                        className="text-[#D4AF37]"
                                    />
                                    <div className="mt-5 text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                                        Category {i + 1} · {d.type}
                                    </div>
                                    <h2 className="mt-3 font-serif text-4xl lg:text-5xl text-[#F7F5EB] leading-tight">
                                        {d.tagline}
                                    </h2>
                                    <p className="mt-5 text-lg text-[#D4AF37]/90 italic">
                                        {d.oneliner}
                                    </p>
                                </div>
                                <div className="lg:col-span-7">
                                    <p className="text-[#F7F5EB]/85 text-base lg:text-lg leading-relaxed">
                                        {d.body}
                                    </p>
                                    <div className="mt-8 bg-[#11203D] border border-[#1F3458] p-6 md:p-8">
                                        <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.24em] font-bold mb-4">
                                            Common examples
                                        </div>
                                        <ul className="grid sm:grid-cols-2 gap-3">
                                            {d.examples.map((ex) => (
                                                <li
                                                    key={ex}
                                                    className="text-sm text-[#9FB3D4] flex gap-3"
                                                >
                                                    <span className="text-[#D4AF37]">
                                                        —
                                                    </span>
                                                    {ex}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* CTA */}
            <section data-testid="damages-cta">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <h2 className="font-serif text-4xl lg:text-6xl text-[#F7F5EB] leading-tight">
                            Wondering what your case is{" "}
                            <span className="italic text-[#D4AF37]">
                                actually worth?
                            </span>
                        </h2>
                        <p className="mt-6 text-[#9FB3D4] text-lg max-w-2xl">
                            A short conversation with Joe is free. He'll walk
                            you through which damages categories likely apply
                            and what we'd need to prove them.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <Link
                            to="/contact"
                            data-testid="damages-cta-link"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                        >
                            Get a Free Damages Assessment{" "}
                            <ArrowRight size={16} />
                        </Link>
                        <div className="mt-5">
                            <Link
                                to="/case-evaluator"
                                data-testid="damages-cta-evaluator"
                                className="text-[#9FB3D4] text-sm underline underline-offset-4 decoration-[#1F3458] hover:text-[#D4AF37] hover:decoration-[#D4AF37] transition-colors"
                            >
                                Prefer to start online? Take the 60-second
                                case evaluator →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
